import { motion } from "framer-motion";
import { Award, BadgeCheck } from "lucide-react";

const certificates = [
  {
    title: "Master of Accounting & Financial Management",
    issuer: "Keller Graduate School of Management · DeVry University",
    tag: "Education",
    detail: "Conferred December 2007 · State of Washington",
  },
  {
    title: "Certified Management Accountant (CMA)",
    issuer: "Institute of Management Accountants",
    tag: "Accounting",
    detail: "Earned January 2019",
  },
  {
    title: "IARCP Lifetime Member",
    issuer: "International Association of Risk & Compliance Professionals",
    tag: "Risk & Compliance",
    detail: "Lifetime membership · December 2018",
  },
  {
    title: "15 ChatGPT Tips for Everyday Data Analysis",
    issuer: "LinkedIn Learning",
    tag: "AI & Analysis",
    detail: "Completed Jul 2026 · Data Analysis, ChatGPT, AI",
  },
  {
    title: "IMA Data Analytics & Visualization Fundamentals Certificate",
    issuer: "Institute of Management Accountants",
    tag: "Data",
    detail: "Awarded May 2020 · Data Analytics, Visualization",
  },
  { title: "QuickBooks Online ProAdvisor", issuer: "Intuit", tag: "Bookkeeping" },
  { title: "Six Sigma Green Belt", issuer: "Process Excellence", tag: "Operations" },
];

const GenericCertificate = () => (
  <div className="border-b border-bone/10 bg-forest/30 p-6" aria-hidden="true">
    <div className="border border-gold/30 bg-pine/70 px-5 py-6 text-center">
      <Award size={20} className="mx-auto mb-3 text-gold" />
      <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-bone/60">Certificate</p>
      <div className="mx-auto mt-3 h-px w-16 bg-gold/40" />
      <div className="mt-4 space-y-1.5">
        <div className="mx-auto h-1 w-3/4 bg-bone/10" />
        <div className="mx-auto h-1 w-1/2 bg-bone/10" />
        <div className="mx-auto h-1 w-2/3 bg-bone/10" />
      </div>
    </div>
  </div>
);

export default function Certificates() {
  return (
    <section id="certificates" data-testid="certificates-section" className="border-t border-linen bg-pine">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          Credentials &amp; Certificates
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="mb-16 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-bone md:text-5xl"
          data-testid="certificates-heading"
        >
          Proof, in <span className="italic text-terra">writing</span>.
        </motion.h2>
        <div className="grid grid-cols-1 gap-px border border-bone/10 bg-bone/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="certificates-grid">
          {certificates.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.76, 0, 0.24, 1] }}
              className="group flex flex-col justify-between bg-pine transition-colors duration-500 hover:bg-forest"
              data-testid={`certificate-card-${i}`}
            >
              <GenericCertificate />
              <div className="flex flex-1 flex-col justify-between p-8 md:p-10">
                <div className="mb-10 flex items-start justify-between">
                  <Award size={28} className="text-terra transition-transform duration-500 group-hover:scale-110" />
                  <span className="border border-bone/20 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-bone/50">
                    {c.tag}
                  </span>
                </div>
                <div>
                  <h3 className="mb-3 font-serif text-xl font-semibold leading-snug text-bone">{c.title}</h3>
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50">
                    <BadgeCheck size={13} className="text-terra" /> {c.issuer}
                  </p>
                  {c.detail && (
                    <p className="mt-2 font-mono text-[10px] tracking-[0.1em] text-bone/40">{c.detail}</p>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.15em] text-bone/40" data-testid="certificates-note">
          Verified credentials — original documents available on request.
        </p>
      </div>
    </section>
  );
}
